// Keeps the company's branding applied while the app is open: restores it on a saved session, refreshes it
// from the database after sign-in, and puts the platform's neutral branding back after sign-out.
// (The login screen itself applies a company's branding the moment its code is entered.)
import { useEffect, useRef } from "react";
import { useAppContext } from "@/contexts/AppContext";
import {
  applyDocumentBranding,
  applyTheme,
  cachedPlatformBranding,
  cachedTenantBranding,
  documentTitle,
  loadMyTenantBranding,
  loadPlatformBranding,
  rememberTenantBranding,
  DEFAULT_PLATFORM,
} from "@/lib/branding";

const BrandingBootstrap = () => {
  const { currentUser } = useAppContext() as any;
  const signedIn = !!currentUser;
  const wasSignedIn = useRef(false);

  useEffect(() => {
    let alive = true;
    const platformNow = cachedPlatformBranding() ?? DEFAULT_PLATFORM;

    if (signedIn) {
      wasSignedIn.current = true;
      // Paint from the saved copy straight away, then refresh from the database.
      const cached = cachedTenantBranding();
      if (cached) {
        applyTheme(cached);
        applyDocumentBranding({ title: documentTitle(platformNow, cached), faviconUrl: cached.favicon_url || platformNow.favicon_url });
      }
      Promise.all([loadMyTenantBranding(), loadPlatformBranding()]).then(([mine, platform]) => {
        if (!alive || !mine) return;
        rememberTenantBranding(mine);
        applyTheme(mine);
        applyDocumentBranding({ title: documentTitle(platform, mine), faviconUrl: mine.favicon_url || platform.favicon_url });
      });
    } else if (wasSignedIn.current) {
      // Signed out: back to the neutral platform look.
      wasSignedIn.current = false;
      rememberTenantBranding(null);
      applyTheme(null);
      applyDocumentBranding({ title: platformNow.product_name, faviconUrl: platformNow.favicon_url });
    }
    return () => {
      alive = false;
    };
  }, [signedIn]);

  return null;
};

export default BrandingBootstrap;
